import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Mail, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "verify_banner_dismissed";

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch (_) {
    return false;
  }
}

export default function VerifyEmailBanner() {
  const { user, loading, refreshMe } = useAuth();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  if (loading || !user || user.email_verified || dismissed) return null;
  if (user.role === "admin") return null;
  if (pathname.startsWith("/verify-email")) return null;

  const resend = async () => {
    setState("sending");
    setError("");
    setNote("");
    try {
      await api.post("/auth/resend-verification");
      setState("sent");
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      setError(typeof detail === "string" ? detail : "Could not send the email. Try again shortly.");
      setState("error");
    }
  };

  const check = async () => {
    setNote("");
    await refreshMe();
    setNote("Not confirmed yet. Click the link in the email first.");
  };

  const close = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch (_) {}
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-40"
      role="status"
    >
      <div
        className="relative bg-[var(--bg-panel)] border border-yellow-400/40 p-4 pr-10"
        style={{ boxShadow: "0 10px 40px -10px rgba(0,0,0,0.6)" }}
      >
        <button
          onClick={close}
          aria-label="Dismiss"
          className="absolute top-2 right-2 text-[var(--text-tertiary)] hover:text-yellow-400 transition"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex gap-3">
          <Mail className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--text-primary)]">Confirm your email</p>
            <p className="mt-1 text-xs text-[var(--text-secondary)] leading-relaxed">
              We sent a link to{" "}
              <span className="text-[var(--text-primary)] break-all">{user.email}</span>. Confirm it
              to unlock the trading challenge.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                onClick={resend}
                disabled={state === "sending"}
                className="bg-yellow-400 text-black font-bold uppercase tracking-wide text-[11px] px-3 py-2 hover:bg-yellow-300 transition disabled:opacity-60"
              >
                {state === "sending" ? "Sending..." : "Resend email"}
              </button>
              <button
                onClick={check}
                className="font-mono text-[11px] uppercase tracking-widest text-[var(--text-secondary)] hover:text-yellow-400 transition px-2 py-2"
              >
                I have confirmed
              </button>
            </div>

            {state === "sent" && (
              <p className="mt-2 text-xs text-green-400">Email sent. Check your inbox and spam folder.</p>
            )}
            {state === "error" && <p className="mt-2 text-xs text-red-400">{error}</p>}
            {note && <p className="mt-2 text-xs text-[var(--text-tertiary)]">{note}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}